'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { cloneElement, isValidElement, PureComponent, forwardRef } from 'react';
import getComponentName from 'react-utils/getComponentName';
import UIBadge from '../../badge/UIBadge';
import UIButton from '../../button/UIButton';
import Small from '../../elements/Small';
import UIIcon from '../../icon/UIIcon';
import UIImage from '../../image/UIImage';
import UICheckbox from '../../input/UICheckbox';
import UIFlex from '../../layout/UIFlex';
import UILoadingSpinner from '../../loading/UILoadingSpinner';
import UITag from '../../tag/UITag';
import { OptionOrGroupType } from '../../types/OptionTypes';
import { stopPropagationHandler } from '../../utils/DomEvents';
import { warnIfFragment } from '../../utils/devWarnings';
import { getComponentPropType } from '../../utils/propTypes/componentProp'; // No-break space ensures that blank items are sized properly

const BLANK_LABEL = '\u00A0';
let instanceCount = 0;

class UITypeaheadResultsItemForwardRef extends PureComponent {
  constructor(props) {
    super(props);
    instanceCount += 1;
    this._checkboxId = `uitri-checkbox-${instanceCount}`;
  }

  getButtonClassName({
    message,
    option
  }) {
    return classNames("uiTypeaheadResults__item", message ? "p-y-2 p-x-5" : "private-typeahead-result-label truncate-text", option.options && !option.selectAll && 'private-typeahead-result-heading');
  }

  getClassName({
    highlighted,
    option: {
      disabled,
      dropdownClassName
    }
  }) {
    return classNames('private-typeahead-result', dropdownClassName, disabled ? 'private-typeahead-result--disabled' : 'private-typeahead-result--selectable' + (highlighted ? " private-typeahead-result--highlighted" : ""));
  }

  getLoadingIndicator(computedButtonText) {
    return /*#__PURE__*/_jsxs(UIFlex, {
      align: "center",
      children: [computedButtonText, /*#__PURE__*/_jsx(UILoadingSpinner, {
        className: "private-typeahead-result__loading-indicator",
        size: "xs",
        use: "tertiary"
      })]
    });
  }

  getLabel({
    option: {
      buttonText,
      dropdownText,
      loading,
      text,
      value
    },
    _useDropdownText
  }) {
    const fallbackText = text || (value == null ? '' : value.toString());

    if (_useDropdownText) {
      return dropdownText == null ? fallbackText : dropdownText;
    }

    const computedButtonText = buttonText == null ? fallbackText : buttonText;

    if (loading) {
      return this.getLoadingIndicator(computedButtonText);
    } else {
      return computedButtonText;
    }
  }

  getTitle({
    option: {
      text,
      title,
      value
    }
  }) {
    if (title === '') {
      return null;
    }

    return title || text || (value == null ? '' : value.toString());
  }

  renderHelp({
    option: {
      disabled,
      help
    }
  }) {
    if (help) {
      return /*#__PURE__*/_jsx(Small, {
        className: "private-typeahead-results-item__help",
        "data-uic-allow-dropdown-close": !disabled ? 'true' : null,
        disabled: disabled,
        use: "help",
        children: help
      });
    }

    return null;
  }

  renderLabel({
    search
  }, {
    computedLabel
  }) {
    if (!computedLabel) {
      return BLANK_LABEL;
    }

    if (!search || typeof computedLabel !== 'string') {
      return computedLabel;
    }

    const searchResult = search.exec(computedLabel);

    if (!searchResult) {
      return computedLabel;
    }

    const match = searchResult[0];
    const head = computedLabel.slice(0, searchResult.index);
    const tail = computedLabel.slice(searchResult.index + match.length);
    return /*#__PURE__*/_jsxs("span", {
      children: [head, /*#__PURE__*/_jsx("span", {
        children: match
      }), tail]
    });
  }

  renderItemImage({
    option: {
      avatar,
      icon,
      iconColor,
      imageUrl
    }
  }) {
    if (avatar) {
      const {
        props: {
          className,
          size
        }
      } = avatar;
      const isUIAvatar = getComponentName(avatar) === 'UIAvatar';
      warnIfFragment(avatar, UITypeaheadResultsItemForwardRef.displayName, 'avatar');
      const avatarEl = /*#__PURE__*/cloneElement(avatar, {
        className: classNames(className, !isUIAvatar && "private-dropdown__item__decoration private-dropdown__item__image"),
        size: isUIAvatar ? 'responsive' : size
      });

      if (!isUIAvatar) {
        return avatarEl;
      } // Resize UIAvatar to match UIImage/UIAvatarTag in UISelects by wrapping in HubStyle selectors (#8748)


      return /*#__PURE__*/_jsx("div", {
        className: "private-dropdown__item__decoration private-dropdown__item__image",
        children: avatarEl
      });
    }

    if (icon) {
      return /*#__PURE__*/_jsx(UIIcon, {
        className: "private-dropdown__item__decoration",
        name: icon,
        color: iconColor
      });
    }

    if (imageUrl) {
      return /*#__PURE__*/_jsx(UIImage, {
        className: "uiTypeaheadResults__image private-dropdown__item__decoration private-dropdown__item__image",
        src: imageUrl
      });
    }

    return null;
  }

  renderTag({
    option: {
      tag
    }
  }) {
    if (!tag) {
      return null;
    }

    return /*#__PURE__*/_jsx(UITag, {
      inline: true,
      use: tag.use,
      children: tag.text
    });
  }

  renderBadge({
    option: {
      badge
    }
  }) {
    if (!badge) {
      return null;
    }

    if ( /*#__PURE__*/isValidElement(badge)) {
      warnIfFragment(badge, UITypeaheadResultsItemForwardRef.displayName, 'badge');
      return /*#__PURE__*/cloneElement(badge, {
        className: classNames(badge.props.className, 'private-dropdown__badge')
      });
    } // Support object params (legacy)


    return /*#__PURE__*/_jsx(UIBadge, {
      className: "private-dropdown__badge",
      use: badge.use,
      children: badge.text
    });
  }

  renderContent({
    renderedItemImage,
    renderedLabel,
    renderedTag,
    renderedBadge,
    toggleable
  }) {
    return /*#__PURE__*/_jsxs("span", {
      className: toggleable ? 'private-dropdown__item--toggleable' : '',
      children: [renderedItemImage, ' ', /*#__PURE__*/_jsx("span", {
        className: "private-dropdown__item__label",
        "data-option-text": true,
        children: renderedLabel
      }), ' ', renderedTag, " ", renderedBadge]
    });
  }

  renderButton(_ref, {
    computedButtonClassName,
    computedTitle,
    renderedContent,
    renderedHelp
  }) {
    let {
      rest: {
        onClick
      }
    } = _ref,
        rest = _objectWithoutPropertiesLoose(_ref.rest, ["onClick"]);

    return (
      /*#__PURE__*/
      // Keyboard events handled by parent component using aria-activedescendant pattern
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events
      _jsxs("span", {
        onClick: onClick,
        children: [/*#__PURE__*/_jsx(UIButton, Object.assign({}, rest, {
          className: computedButtonClassName,
          tabIndex: -1,
          title: computedTitle,
          use: "unstyled",
          children: renderedContent
        })), renderedHelp]
      })
    );
  }

  renderCheckbox({
    isIndeterminate,
    isSelected,
    onToggle,
    option,
    style
  }, {
    computedTitle,
    renderedContent,
    renderedHelp
  }) {
    return /*#__PURE__*/_jsxs("label", {
      // Route clicks on help text to checkbox
      style: {
        cursor: 'pointer'
      },
      onMouseDown: stopPropagationHandler // Prevent double-toggle
      ,
      children: [/*#__PURE__*/_jsx(UICheckbox, {
        className: "private-typeahead-result-label",
        checked: isSelected,
        disabled: option.disabled || isSelected && option.clearableValue === false,
        id: this._checkboxId,
        indeterminate: isIndeterminate,
        onChange: onToggle,
        style: style,
        title: computedTitle,
        children: renderedContent
      }), renderedHelp]
    });
  }

  render() {
    const _this$props = this.props,
          {
      forwardedRef,
      highlighted,
      id,
      isIndeterminate,
      isSelected,
      itemComponent: Item,
      message,
      onMouseEnter,
      onToggle,
      option,
      search,
      style,
      toggleable,
      _useButton,
      _useDropdownText
    } = _this$props,
          rest = _objectWithoutPropertiesLoose(_this$props, ["forwardedRef", "highlighted", "id", "isIndeterminate", "isSelected", "itemComponent", "message", "onMouseEnter", "onToggle", "option", "search", "style", "toggleable", "_useButton", "_useDropdownText"]); // Escape hatch for dividers


    if (option.rawNode) return option.rawNode;
    const computedAriaSelected = highlighted || null;
    const computedItemClassName = this.getClassName({
      highlighted,
      option
    });
    const computedButtonClassName = this.getButtonClassName({
      message,
      option
    });
    const computedTitle = this.getTitle({
      option
    });
    const computedLabel = this.getLabel({
      option,
      _useDropdownText
    });
    const renderedItemImage = this.renderItemImage({
      option
    });
    const renderedLabel = this.renderLabel({
      search
    }, {
      computedLabel
    });
    const renderedTag = this.renderTag({
      option
    });
    const renderedBadge = this.renderBadge({
      option
    });
    const renderedHelp = this.renderHelp({
      option,
      toggleable
    });
    let renderedContent = this.renderContent({
      renderedItemImage,
      renderedLabel,
      renderedTag,
      renderedBadge,
      toggleable
    }); // If _useButton={true}, wrap content in a button

    renderedContent = _useButton ? this.renderButton({
      rest
    }, {
      computedButtonClassName,
      computedTitle,
      renderedContent,
      renderedHelp
    }) : renderedContent; // If toggleable={true}, wrap content in a checkbox

    renderedContent = toggleable ? this.renderCheckbox({
      isIndeterminate,
      isSelected,
      onToggle,
      option,
      style
    }, {
      computedTitle,
      renderedContent,
      renderedHelp
    }) : renderedContent;

    if (typeof Item === 'function') {
      return /*#__PURE__*/_jsx(Item, Object.assign({}, rest, {
        "aria-selected": computedAriaSelected,
        id: id,
        onMouseEnter: onMouseEnter,
        option: option,
        className: computedItemClassName,
        "data-option-value": option.value,
        role: "option",
        children: renderedContent
      }));
    }

    return /*#__PURE__*/_jsx(Item, {
      "aria-selected": computedAriaSelected,
      className: computedItemClassName,
      "data-option-value": option.value,
      id: id,
      onMouseEnter: onMouseEnter,
      role: "option",
      ref: forwardedRef,
      children: renderedContent
    });
  }

}

const UITypeaheadResultsItem = /*#__PURE__*/forwardRef((props, ref) => {
  return /*#__PURE__*/_jsx(UITypeaheadResultsItemForwardRef, Object.assign({}, props, {
    forwardedRef: ref
  }));
});
export default UITypeaheadResultsItem;
UITypeaheadResultsItemForwardRef.displayName = 'UITypeaheadResultsItem';
UITypeaheadResultsItem.propTypes = {
  highlighted: PropTypes.bool.isRequired,
  itemComponent: getComponentPropType({
    propTypes: {
      'aria-selected': PropTypes.bool,
      'data-option-value': PropTypes.string,
      id: PropTypes.string,
      className: PropTypes.string,
      option: OptionOrGroupType,
      onMouseEnter: PropTypes.func,
      children: PropTypes.node
    }
  }),
  isIndeterminate: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  message: PropTypes.bool,
  onToggle: PropTypes.func,
  option: OptionOrGroupType.isRequired,
  search: PropTypes.instanceOf(RegExp),
  toggleable: PropTypes.bool.isRequired,
  _useButton: PropTypes.bool,
  _useDropdownText: PropTypes.bool
};
UITypeaheadResultsItem.defaultProps = {
  highlighted: false,
  isIndeterminate: false,
  isSelected: false,
  itemComponent: 'li',
  toggleable: false,
  _useButton: true,
  _useDropdownText: true
};